/**
 * AppPricingSection — the ONE pricing section every Colony marketing site renders (APP-ENTITLEMENT-001 Phase 11):
 * the anchors as tier cards, the apps beside the featured one, the free apps, the trial terms, the published
 * processing rate and the three-store worked example — every number the public catalog's
 * (`GET /v1/catalog/apps`), every total `quoteBasket`'s, every CTA the ONE signup URL. Nine sites, no price typed
 * on any of them.
 *
 * While the catalog loads the section shows tier skeletons; a failed read says so with Retry and never renders an
 * empty price list as if nothing were for sale (RES-PRIM-001).
 *
 * @module @puffin-core/marketing-shell/pricing/AppPricingSection
 */

import { useState } from 'react';
import PropTypes from 'prop-types';
import { BILLING_CYCLE, TRIAL_DAYS } from '@puffin-core/app-catalog';
import { InlineFeedback } from '@puffin-core/feedback-ui';
import { useFormatUsdFromCents } from '@puffin-core/money';
import { SkeletonCard } from '@puffin-core/skeleton-kit';

import { useMarketingShellTranslate } from '../hooks/useMarketingShellTranslate.js';
import { AddOnRows } from './AddOnRows.jsx';
import { AnchorPricingTable } from './AnchorPricingTable.jsx';
import { BillingCycleToggle } from './BillingCycleToggle.jsx';
import { FreeAppsCallout } from './FreeAppsCallout.jsx';
import { ProcessingRateLine } from './ProcessingRateLine.jsx';
import { ThreeStoreExample } from './ThreeStoreExample.jsx';
import { featuredAnchorKeyOf } from './pricingRules.js';

/** How many tier placeholders the skeleton shows while the catalog loads: the three anchors. */
const SKELETON_TIERS = 3;

/**
 * The cycle a visitor first sees. Declared as the whole union so the state it seeds can hold either cycle: the frozen
 * `BILLING_CYCLE.MONTHLY` alone would type that state as `'monthly'` only.
 *
 * @type {import('./BillingCycleToggle.jsx').BillingCycle}
 */
const INITIAL_BILLING_CYCLE = BILLING_CYCLE.MONTHLY;

function PricingSkeleton({ label }) {
  return (
    <div className="tier-grid" role="status" aria-label={label} data-testid="pricing-skeleton">
      {Array.from({ length: SKELETON_TIERS }, (_, index) => (
        <SkeletonCard key={index} showTitle textLines={4} showAction aria-hidden="true" />
      ))}
    </div>
  );
}

PricingSkeleton.propTypes = { label: PropTypes.string.isRequired };

/**
 * @param {Object} props
 * @param {string} props.appKey - the app whose marketing site this is (its anchor is featured; its row is marked).
 * @param {{status: string, data?: Object, retry: () => void}} props.catalog - the public catalog as a RESOURCE
 *   (`useAppCatalog`). The PAGE owns the read, so its JSON-LD is built from the same data this section renders.
 * @param {string} [props.fromOrigin] - this site's origin, so a CTA on the wizard's own site stays a router link.
 * @param {Function} [props.t] - the host's translator, honoured first.
 * @param {(href: string, destinationKind: string) => void} [props.onOutboundClick] - a click on a CTA that leaves
 *   the site.
 * @returns {React.JSX.Element}
 */
export function AppPricingSection({ appKey, catalog, fromOrigin, t: hostT, onOutboundClick }) {
  const [billingCycle, setBillingCycle] = useState(INITIAL_BILLING_CYCLE);
  const t = useMarketingShellTranslate(hostT);
  const formatPrice = useFormatUsdFromCents();

  if (catalog.status === 'error') {
    return (
      <InlineFeedback
        tone="warning"
        message={t('pricing.loadFailed')}
        actions={[{ key: 'retry', label: t('pricing.retry'), onClick: catalog.retry }]}
        testId="pricing-load-failed"
      />
    );
  }
  if (catalog.status !== 'ready') {
    return <PricingSkeleton label={t('pricing.loading')} />;
  }

  const data = catalog.data;
  const featuredAnchorKey = featuredAnchorKeyOf(data, appKey);
  if (featuredAnchorKey === null) return null;

  return (
    <div data-testid="app-pricing-section">
      <div className="flex-row justify-center mb-md">
        <BillingCycleToggle billingCycle={billingCycle} onCycleChange={setBillingCycle} t={hostT} />
      </div>
      <AnchorPricingTable
        catalog={data}
        siteAppKey={appKey}
        featuredAnchorKey={featuredAnchorKey}
        billingCycle={billingCycle}
        fromOrigin={fromOrigin}
        formatPrice={formatPrice}
        t={hostT}
        onOutboundClick={onOutboundClick}
      />
      <p className="text-sm text-muted" data-testid="pricing-trial-terms">
        {t('pricing.trialTerms', { days: String(data.trialDays ?? TRIAL_DAYS) })}
      </p>
      <AddOnRows
        catalog={data}
        anchorAppKey={featuredAnchorKey}
        siteAppKey={appKey}
        fromOrigin={fromOrigin}
        formatPrice={formatPrice}
        t={hostT}
        onOutboundClick={onOutboundClick}
      />
      <FreeAppsCallout catalog={data} anchorAppKey={featuredAnchorKey} t={hostT} />
      <ProcessingRateLine processing={data.processing} formatPrice={formatPrice} t={hostT} />
      <ThreeStoreExample
        catalog={data}
        anchorAppKey={featuredAnchorKey}
        siteAppKey={appKey}
        formatPrice={formatPrice}
        t={hostT}
      />
    </div>
  );
}

AppPricingSection.propTypes = {
  appKey: PropTypes.string.isRequired,
  catalog: PropTypes.shape({
    status: PropTypes.string.isRequired,
    data: PropTypes.object,
    retry: PropTypes.func.isRequired,
  }).isRequired,
  fromOrigin: PropTypes.string,
  t: PropTypes.func,
  onOutboundClick: PropTypes.func,
};

export default AppPricingSection;
